import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import TbxLoadingPage from '@/common/components/TbxLoadingOverlay/TbxLoadingOverlay'
import {
    clearAuthProvider,
    getAuthProvider,
    getRegistrationProvider,
    setAuthProvider,
} from '@/common/helpers/auth/authProviderDetection'
import { useAuth } from '@/common/hooks/useAuth'

/**
 * Register - User registration page
 *
 * Initiates the registration flow using the unified auth hook.
 * For new users with no cached provider, sets the registration provider
 * (controlled by VITE_REGISTRATION_PROVIDER env var) and reloads so
 * AuthProvider mounts the correct SDK before calling register().
 *
 * @returns {JSX.Element}
 */
const Register = () => {
    const navigate = useNavigate()
    const { isAuthenticated, isLoading, provider, register } = useAuth()
    const isProcessingRef = useRef(false)
    const hasAttemptedLoginRef = useRef(false)

    // For new users with no cached provider, set the registration provider and
    // reload so AuthProvider mounts the correct SDK (MSAL or Kinde).
    useEffect(() => {
        const cachedProvider = getAuthProvider()
        if (!cachedProvider) {
            const registrationProvider = getRegistrationProvider()
            console.debug('[Register] No cached provider, setting registration provider:', registrationProvider)
            setAuthProvider(registrationProvider)
            window.location.href = '/register'
        }
    }, [])

    const handleSignUp = useCallback(async () => {
        // Multiple layers of protection against concurrent executions
        if (isProcessingRef.current || hasAttemptedLoginRef.current) {
            console.debug('[Register] Already processing or attempted, skipping...')
            return
        }

        // Guard: skip if cached provider doesn't match mounted AuthProvider SDK.
        // After bootstrap sets the provider in localStorage and triggers a page reload,
        // Chrome continues executing JS before navigation — the auth context still has
        // the old provider mounted. Calling register() here would use the wrong SDK and
        // fail, clearing the cache and causing an infinite reload loop.
        const cachedProvider = getAuthProvider()
        if (cachedProvider && cachedProvider !== provider) {
            console.debug('[Register] Provider mismatch (bootstrap pending), skipping...')
            return
        }

        // Check auth loading status - provider-agnostic check
        if (isLoading) {
            console.debug('[Register] Auth interaction in progress, skipping...')
            return
        }

        console.debug('[Register] Starting registration process')
        isProcessingRef.current = true
        hasAttemptedLoginRef.current = true

        try {
            // Check if already authenticated
            if (isAuthenticated) {
                console.debug('[Register] User already logged in, redirecting to home')
                navigate('/')
                return
            }

            // Read login_hint from URL if present (redirected from Login.jsx email-first flow)
            const params = new URLSearchParams(window.location.search)
            const loginHint = params.get('login_hint')
            const options = loginHint ? { login_hint: loginHint, loginHint } : {}

            console.debug('[Register] Starting registration redirect')
            await register(options)
        } catch (error) {
            console.error('[Register] Error during sign up process:', error)
            clearAuthProvider()
            isProcessingRef.current = false
            hasAttemptedLoginRef.current = false
        }
    }, [isAuthenticated, isLoading, navigate, provider, register])

    useEffect(() => {
        handleSignUp()
    }, [handleSignUp])

    return <TbxLoadingPage />
}

export default Register
